import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Stack,
  useTheme,
  Link,
  IconButton,
  TextField,
  Button,
  Grid
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  Facebook,
  Twitter,
  Instagram,
  YouTube,
  LinkedIn
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  const theme = useTheme();

  const sections = [
    {
      title: "Information We Collect",
      content: [
        "Personal Information: We may collect personal information such as your name, email address, and contact details when you subscribe to our newsletter or contact us through our contact form.",
        "Usage Data: We automatically collect information about how you interact with our website, including pages visited, time spent on pages, and links clicked.",
        "Device Information: We may collect information about your device, including IP address, browser type, operating system, and device identifiers.",
        "Cookies: We use cookies and similar tracking technologies to enhance your browsing experience and analyze website traffic."
      ]
    },
    {
      title: "How We Use Your Information",
      content: [
        "To provide and maintain our news services",
        "To send you newsletters and updates about Ethiopia and East Africa (if you subscribe)",
        "To improve our website functionality and user experience",
        "To analyze usage patterns and optimize content delivery",
        "To respond to your inquiries and provide customer support",
        "To comply with legal obligations and protect our rights"
      ]
    },
    {
      title: "Information Sharing",
      content: [
        "We do not sell, trade, or rent your personal information to third parties.",
        "We may share your information with trusted service providers who assist us in operating our website and providing services.",
        "We may disclose information if required by law or to protect our rights and safety.",
        "Aggregated, anonymized data may be shared for analytical purposes."
      ]
    },
    {
      title: "Data Security",
      content: [
        "We implement appropriate security measures to protect your personal information.",
        "We use encryption and secure protocols to safeguard data transmission.",
        "We regularly review and update our security practices.",
        "However, no method of transmission over the internet is 100% secure."
      ]
    },
    {
      title: "Your Rights",
      content: [
        "Access: You have the right to access your personal information.",
        "Correction: You can request correction of inaccurate information.",
        "Deletion: You may request deletion of your personal information.",
        "Opt-out: You can unsubscribe from newsletters and marketing communications.",
        "Data Portability: You can request a copy of your data in a portable format.",
        "Contact: You can contact us at any time to exercise these rights."
      ]
    },
    {
      title: "Cookies and Tracking",
      content: [
        "We use essential cookies for website functionality.",
        "Analytics cookies help us understand how visitors use our site.",
        "You can control cookie settings through your browser preferences.",
        "Some features may not work properly if cookies are disabled."
      ]
    },
    {
      title: "Third-Party Services",
      content: [
        "We may use third-party services for analytics, advertising, and social media integration.",
        "These services have their own privacy policies and data practices.",
        "We encourage you to review their privacy policies.",
        "We are not responsible for the practices of third-party services."
      ]
    },
    {
      title: "Children's Privacy",
      content: [
        "Our services are not intended for children under 13 years of age.",
        "We do not knowingly collect personal information from children under 13.",
        "If we become aware of such collection, we will take steps to delete the information.",
        "Parents or guardians should contact us if they believe we have collected children's data."
      ]
    },
    {
      title: "International Data Transfers",
      content: [
        "Your information may be transferred to and processed in countries other than your own.",
        "We ensure appropriate safeguards are in place for international transfers.",
        "We comply with applicable data protection laws and regulations.",
        "By using our services, you consent to such transfers."
      ]
    },
    {
      title: "Changes to This Policy",
      content: [
        "We may update this Privacy Policy from time to time.",
        "We will notify you of any material changes via email or website notice.",
        "Your continued use of our services constitutes acceptance of updated policies.",
        "We encourage you to review this policy periodically."
      ]
    }
  ];

  return (
    <>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            py: 6,
            position: 'relative'
          }}
        >
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                align="center"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                Privacy Policy
              </Typography>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  opacity: 0.9,
                  maxWidth: '800px',
                  mx: 'auto'
                }}
              >
                How we collect, use, and protect your personal information
              </Typography>
            </motion.div>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 6 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                At EthioReview, we are committed to protecting your privacy and ensuring the security of your personal information. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
                and use our services.
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Last updated:</strong> {new Date().toLocaleDateString()}
              </Typography>
            </Paper>
          </motion.div>

          {/* Policy Sections */}
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
            >
              <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
                <Typography
                  variant="h5"
                  component="h2"
                  gutterBottom
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main', 
                    mb: 3,
                    fontSize: { xs: '1.5rem', md: '1.75rem' }
                  }}
                >
                  {section.title}
                </Typography>
                <Stack spacing={2}>
                  {section.content.map((item, itemIndex) => (
                    <Typography 
                      key={itemIndex} 
                      variant="body1" 
                      sx={{ 
                        lineHeight: 1.7,
                        pl: 2,
                        borderLeft: `3px solid ${theme.palette.primary.light}`,
                        ml: 1
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </Paper>
            </motion.div>
          ))}

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
          >
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Contact Us
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3 }}>
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </Typography>
              <Stack spacing={2} alignItems="center">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Email color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">privacy@ethioreview.com</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">+251 123 456 789</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOn color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">123 News Street, Addis Ababa, Ethiopia</Typography>
                </Box>
              </Stack>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          px: 2,
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Quick Links */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Quick Links
              </Typography>
              <Stack spacing={1}>
                {[
                  { name: "Home", path: "/" },
                  { name: "About Us", path: "/about" },
                  { name: "Contact", path: "/contact" }
                ].map((item) => (
                  <motion.div whileHover={{ x: 5 }} key={item.name}>
                    <Link
                      to={item.path}
                      style={{
                        display: "block",
                        color: "inherit",
                        textDecoration: "none",
                      }}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
              </Stack>
            </Grid>

            {/* Categories */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Categories
              </Typography>
              <Stack spacing={1}>
                {["Politics", "Business", "Sports"].map((category) => (
                  <motion.div whileHover={{ x: 5 }} key={category}>
                    <Link
                      to={`/${category.toLowerCase()}-news`}
                      style={{
                        display: "block",
                        color: "inherit",
                        textDecoration: "none",
                      }}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {category}
                    </Link>
                  </motion.div>
                ))}
              </Stack>
            </Grid>

            {/* Contact Info */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Contact Us
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LocationOn color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    123 News Street, Addis Ababa, Ethiopia
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Phone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">+251 123 456 789</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Email color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">info@ethioreview.com</Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Social Media & Newsletter */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Follow Us
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Facebook />, color: "#3b5998", name: "Facebook" },
                  { icon: <Twitter />, color: "#1DA1F2", name: "Twitter" },
                  { icon: <Instagram />, color: "#E1306C", name: "Instagram" },
                  { icon: <YouTube />, color: "#FF0000", name: "YouTube" },
                  { icon: <LinkedIn />, color: "#0077B5", name: "LinkedIn" },
                ].map((social) => (
                  <motion.div whileHover={{ scale: 1.1 }} key={social.name}>
                    <IconButton
                      href="#"
                      target="_blank"
                      rel="noopener"
                      sx={{
                        backgroundColor: `${social.color}10`,
                        color: social.color,
                        "&:hover": { backgroundColor: `${social.color}20` },
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  </motion.div>
                ))}
              </Stack>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Newsletter
              </Typography>
              <Box component="form" sx={{ display: "flex" }}>
                <TextField
                  size="small"
                  placeholder="Your email"
                  variant="outlined"
                  fullWidth
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Copyright & Legal */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} EthioReview. All rights reserved.
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
              {[
                { name: "Privacy Policy", path: "/privacy" },
                { name: "Terms of Service", path: "/terms" },
                { name: "Cookie Policy", path: "/cookies" }
              ].map((item) => (
                <Typography
                  key={item.name}
                  component={Link}
                  to={item.path}
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textDecoration: "none",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {item.name}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default PrivacyPolicy;
